import AuthContext from '../../store/auth-context';
import SETTING_KEYS from '../../constants/setting-keys';
import { RegionalInfo, useImplantContext } from '../../store/implant-context';
import { UserFile } from '../files/FilesOverviewPage';
import ImplantImageComponent from './info/ImplantImageComponent';
import ImplantInfoComponent, {
    HistoryVersion,
} from './info/ImplantInfoComponent';
import { useContext, useState, useEffect } from 'react';
import RIGHTS_KEYS from '../../constants/rights-keys';
import styles from './Procedure.module.css';
import BackgroundContainer from '../ui-components/BackgroundContainer';
import ApprovedComponent from '../approval/ApprovedComponent';
import { ImplantFoldersDisplay } from '../group/ImplantFoldersDisplay';
import PolicyLink from './department-tools/PolicyLink';
import MRICompatibilityDialog from './mri-compatibility/MRICompatibilityDialog';
import GoToReportingButton from '../reports/GoToReportingButton';
import ImplantDetailsComponent from './info/ImplantDetailsComponent';
import ImplantNoteComponent from './notes/ImplantNoteComponent';
import ConditionalsTable from './conditionals/ConditionalsTable';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import ConditionalsComponent from './conditionals/ConditionalsComponent';
import { getFlagForRegion, onlyUnique } from '../../utils/utils';
import TabContainer from './TabContainer';
import { useTranslation } from 'react-i18next';
import Flag from '../ui-components/Flag';
import { ImplantPreviewDialog } from './preview/ImplantPreviewDialog';
import { resolveLastCheckedDate } from './implantDateUtils';

const isEmpty = (value: any) => {
    return (
        value === undefined ||
        value === null ||
        value === '' ||
        value === '<p></p>'
    );
};

const RegionalImplant = ({
    historyVersions,
    selectedVersion,
    handleHistoryChange,
    withDeptTools = true,
    isPreview = false,
}: {
    historyVersions: HistoryVersion[];
    selectedVersion: HistoryVersion;
    handleHistoryChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    withDeptTools?: boolean;
    isPreview?: boolean;
}) => {
    const ctx = useContext(AuthContext);
    const { info, regionalInfos, conditionals } = useImplantContext();

    const { t } = useTranslation();

    const [currentRegionalIndex, setCurrentRegionalIndex] = useState<number>(0);
    const [currentConditional, setCurrentConditional] = useState<any>(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const [currentRegionalInfo, setCurrentRegionalInfo] =
        useState<RegionalInfo>(regionalInfos[0]);

    useEffect(() => {
        if (ctx.getContext()?.region && regionalInfos?.length > 0) {
            const regionalIndex = regionalInfos.findIndex((r) =>
                r?.regions?.includes(ctx.getContext().region)
            );
            setCurrentRegionalIndex(regionalIndex !== -1 ? regionalIndex : 0);
        } else {
            setCurrentRegionalIndex(0);
        }
    }, [ctx]);

    useEffect(() => {
        const selectedConditional = conditionals.find(
            (conditional: any) =>
                conditional._id ===
                regionalInfos[currentRegionalIndex]?.conditionals
        );
        setCurrentConditional(selectedConditional);
        setCurrentRegionalInfo(regionalInfos[currentRegionalIndex]);
    }, [regionalInfos, currentRegionalIndex, conditionals]);

    const getRegionalButtonLabel = (
        regionalInfo: RegionalInfo
    ): React.ReactNode => {
        if (
            !regionalInfo ||
            !regionalInfo.regions ||
            regionalInfo.regions.length === 0
        ) {
            return 'Unknown region';
        }
        return regionalInfo.regions
            .map((r) => (r === 'UK' ? 'EU' : r))
            .filter(onlyUnique)
            .map((region) => (
                <div className="flex flex-row gap-2 items-center" key={region}>
                    <Flag flag={region.toLowerCase()} /> {region}
                </div>
            ));
    };

    return (
        <>
            {currentRegionalInfo && (
                <div className={!isPreview ? styles.implant : ''}>
                    {isPreview &&
                        ctx.orRights([RIGHTS_KEYS.manageAllImplants]) && (
                            <BackgroundContainer>
                                {resolveLastCheckedDate(
                                    info,
                                    currentRegionalInfo
                                ) && (
                                    <p>
                                        {t('implant.preview.lastChecked')}:{' '}
                                        {new Date(
                                            resolveLastCheckedDate(
                                                info,
                                                currentRegionalInfo
                                            ) as any
                                        ).toLocaleDateString()}
                                    </p>
                                )}
                                {regionalInfos.map((regionalInfo) => (
                                    <>
                                        {regionalInfo?.regions?.length > 0 && (
                                            <p>
                                                Region(s):{' '}
                                                {regionalInfo.regions.join(
                                                    ', '
                                                )}
                                            </p>
                                        )}
                                        {regionalInfo.sources?.length > 0 &&
                                            regionalInfo.sources.map(
                                                (source) => (
                                                    <div
                                                        key={source.title}
                                                        className="flex flex-row gap-2 items-center"
                                                    >
                                                        <p>{source.title}</p>
                                                        <p>{source.content}</p>
                                                        {source.documentDate && (
                                                            <p>
                                                                document date:
                                                                {new Date(
                                                                    source.documentDate.toString()
                                                                ).toLocaleDateString()}
                                                            </p>
                                                        )}
                                                    </div>
                                                )
                                            )}
                                    </>
                                ))}
                            </BackgroundContainer>
                        )}
                    <div className={styles.infoWrapper}>
                        <div className={styles.info}>
                            <ImplantInfoComponent
                                info={{
                                    _id: info._id,
                                    model: currentRegionalInfo.model,
                                    manufacturer:
                                        info[SETTING_KEYS.manufacturer],
                                    type: info[SETTING_KEYS.type],
                                    mriStatus: currentRegionalInfo.mriStatus,
                                    verifiedByNI:
                                        (currentRegionalInfo as any)?.[
                                            SETTING_KEYS.verifiedByNI
                                        ] ??
                                        (currentRegionalInfo as any)?.verifiedByNI ??
                                        info[SETTING_KEYS.verifiedByNI],
                                    mriCode: info.mriCode,
                                    ifu: currentRegionalInfo.ifu as UserFile,
                                    filename:
                                        currentRegionalInfo[
                                            SETTING_KEYS.filename
                                        ] || info[SETTING_KEYS.filename],
                                    updatedAt: info.updatedAt,
                                }}
                                historyVersions={historyVersions}
                                selectedVersion={selectedVersion}
                                handleChange={handleHistoryChange}
                                onPreview={
                                    !isPreview && withDeptTools
                                        ? () => setPreviewOpen(true)
                                        : undefined
                                }
                            />
                        </div>
                        <div className={styles.image}>
                            {ctx.orRights([RIGHTS_KEYS.detailedInfo]) && (
                                <ImplantImageComponent
                                    name={
                                        currentRegionalInfo[SETTING_KEYS.image]
                                    }
                                    file={currentRegionalInfo.image as UserFile}
                                />
                            )}
                        </div>
                    </div>
                    <div className={styles.detailsWrapper}>
                        {!isPreview && (
                            <div className={styles.deptTools}>
                                {ctx.orRights([RIGHTS_KEYS.deptTools]) && (
                                    <BackgroundContainer>
                                        <div className={styles.deptActions}>
                                            {withDeptTools && (
                                                <div
                                                    className={
                                                        styles.deptActions
                                                    }
                                                >
                                                    <ApprovedComponent
                                                        implantId={info._id}
                                                    />
                                                    <ImplantFoldersDisplay
                                                        implantId={info._id}
                                                    />
                                                    <PolicyLink
                                                        implantId={info._id}
                                                    />
                                                    {currentRegionalInfo?.mriStatus ===
                                                        2 && (
                                                        <MRICompatibilityDialog
                                                            conditionalId={
                                                                currentRegionalInfo?.conditionals?.toString() ??
                                                                null
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </BackgroundContainer>
                                )}
                            </div>
                        )}
                        <div className={styles.details}>
                            <ImplantDetailsComponent
                                info={{
                                    model: currentRegionalInfo.model,
                                    otherFiles: currentRegionalInfo.otherFiles,
                                    category: info[SETTING_KEYS.category],
                                    distEndDate:
                                        currentRegionalInfo.distEndDate instanceof
                                        Date
                                            ? currentRegionalInfo.distEndDate.toISOString()
                                            : currentRegionalInfo.distEndDate,
                                    bodyPlacement:
                                        info[SETTING_KEYS.bodyPlacement],
                                    catalogNumbers:
                                        currentRegionalInfo.catalogNumbers,
                                    maxStentLength:
                                        info[SETTING_KEYS.maxStentLength],
                                    gmdnDefinition:
                                        info[SETTING_KEYS.gmdnDefinition],
                                    drugType: info[SETTING_KEYS.drugType],
                                }}
                                hasToggle={!isPreview}
                            />
                            {withDeptTools && (
                                <div style={{ marginBottom: '30px' }}>
                                    <ImplantNoteComponent
                                        implantId={info._id}
                                    />
                                </div>
                            )}
                            <TabContainer
                                tabs={regionalInfos.map((r) =>
                                    getRegionalButtonLabel(r)
                                )}
                                currentTab={getRegionalButtonLabel(
                                    regionalInfos[currentRegionalIndex]
                                )}
                                onChange={setCurrentRegionalIndex}
                            >
                                {!isEmpty(
                                    currentConditional?.[SETTING_KEYS.coilType]
                                ) && (
                                    <div className={styles.customField}>
                                        <ConditionalsTable
                                            style="critical"
                                            headers={[
                                                <>
                                                    <ReportGmailerrorredIcon />
                                                    {SETTING_KEYS.coilType}
                                                </>,
                                            ]}
                                            fields={[
                                                [
                                                    currentConditional[
                                                        SETTING_KEYS.coilType
                                                    ],
                                                ],
                                            ]}
                                        />
                                    </div>
                                )}
                                {currentRegionalInfo.customFields?.some(
                                    (field: any) => field.isCritical
                                ) &&
                                    currentRegionalInfo.customFields
                                        .filter(
                                            (field: any) => field.isCritical
                                        )
                                        .map((field: any) => {
                                            if (
                                                field === undefined ||
                                                field === null ||
                                                field === ''
                                            ) {
                                                return '';
                                            }
                                            return (
                                                <div
                                                    className={
                                                        styles.customField
                                                    }
                                                >
                                                    <ConditionalsTable
                                                        style="critical"
                                                        headers={[
                                                            <>
                                                                <ReportGmailerrorredIcon />
                                                                {field.heading}
                                                            </>,
                                                        ]}
                                                        fields={[[field.value]]}
                                                    />
                                                </div>
                                            );
                                        })}
                                {currentRegionalInfo.precaution && (
                                    <div style={{ marginBottom: '30px' }}>
                                        <ConditionalsTable
                                            isInfo
                                            title={'Precaution'}
                                            style="info"
                                            headers={[
                                                <>
                                                    <ReportGmailerrorredIcon />
                                                    Precaution
                                                </>,
                                            ]}
                                            fields={[
                                                [
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: currentRegionalInfo.precaution,
                                                        }}
                                                    ></div>,
                                                ],
                                            ]}
                                        />
                                    </div>
                                )}
                                {currentRegionalInfo.mriStatus === 2 && (
                                    <ConditionalsComponent
                                        conditionals={currentConditional}
                                    />
                                )}

                                {!isEmpty(info[SETTING_KEYS.notes]) && (
                                    <div className={styles.customField}>
                                        <ConditionalsTable
                                            headers={[SETTING_KEYS.notes]}
                                            fields={[
                                                [info[SETTING_KEYS.notes]],
                                            ]}
                                        />
                                    </div>
                                )}
                                {currentRegionalInfo.customFields?.some(
                                    (field: any) => !field.isCritical
                                ) &&
                                    currentRegionalInfo.customFields
                                        .filter(
                                            (field: any) => !field.isCritical
                                        )
                                        .map((field: any) => {
                                            if (isEmpty(field)) {
                                                return '';
                                            }
                                            return (
                                                <div
                                                    className={
                                                        styles.customField
                                                    }
                                                >
                                                    <ConditionalsTable
                                                        headers={[
                                                            field.heading,
                                                        ]}
                                                        fields={[[field.value]]}
                                                    />
                                                </div>
                                            );
                                        })}
                            </TabContainer>
                        </div>
                    </div>
                </div>
            )}
            {currentRegionalInfo && (
                <ImplantPreviewDialog
                    open={previewOpen}
                    onOpenChange={setPreviewOpen}
                    info={info}
                    regionalInfo={currentRegionalInfo}
                    conditionals={currentConditional}
                />
            )}
        </>
    );
};

export default RegionalImplant;
